<template>
  <b-form-group>
    <v-select
      v-model="selectedSalesType"
      :options="salesTypeOptions"
      :filterable="false"
      placeholder="Select Sales Type"
      :loading="loadingSalesTypes || loadingSelectedSalesType"
      :clearable="true"
      @search="onSearch"
    >
      <template #no-options>
        <div class="text-center text-muted">No sales types found</div>
      </template>

      <template #spinner="{ loading }">
        <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </template>

      <template #selected-option="{ id, code, name }">
        <small class="text-muted mr-1"> </small>
        <strong class="mr-1">{{ id }} | {{ name }}</strong>
      </template>

      <template #option="{ id, name, code, description, isActive }">
        <b-row>
          <b-col md="12">
            <small class="text-muted d-block">{{ id }}</small>
          </b-col>
          <b-col md="12">
            <b-row>
              <b-col md="12">
                <strong class="mr-1">{{ name }}</strong>
                <span>{{ code }}</span>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="8">
                <div v-if="description">
                  <small class="text-muted d-block">
                    {{ description }}
                  </small>
                </div>
              </b-col>

              <b-col md="4" class="text-right">
                <small class="d-block"> </small>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </template>
    </v-select>
  </b-form-group>
</template>

<script>
import { BFormGroup } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { BCol, BRow } from 'bootstrap-vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import SalesTypeStoreModule from '../../sales-type/SalesTypeStoreModule';

export default {
  name: 'SalesTypeDropdownFilter',
  components: {
    vSelect,
    BRow,
    BCol,
    BFormGroup,
  },
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'Sales Type',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
  },
  setup(props, { emit }) {
    const SALES_TYPE_STORE_MODULE_NAME = 'sales-type-filter';

    // Register module
    if (!store.hasModule(SALES_TYPE_STORE_MODULE_NAME))
      store.registerModule(SALES_TYPE_STORE_MODULE_NAME, SalesTypeStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SALES_TYPE_STORE_MODULE_NAME))
        store.unregisterModule(SALES_TYPE_STORE_MODULE_NAME);
    });

    // Reactive references
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingSalesTypes = ref(false);
    const loadingSelectedSalesType = ref(false);
    const salesTypeOptions = ref([]);
    const selectedSalesType = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);
    // Methods
    const fetchSalesTypes = async () => {
      loadingSalesTypes.value = true;
      try {
        const response = await api.get(`/SalesTypes/GetByPagination/${businessID.value}`, {
          params: {
            searchTerm: searchTerm.value,
            pageNumber: pageNumber.value,
            pageSize: 999,
          },
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          salesTypeOptions.value = [...response.data.data.items];
        } else {
          console.error(
            '=== SalesTypeDropdownFilter options Error in paginated sales type data format:',
            response.data
          );
          salesTypeOptions.value = [];
        }
      } catch (error) {
        console.error('=== SalesTypeDropdownFilter options Error fetching sales types:', error);
      } finally {
        loadingSalesTypes.value = false;
      }
    };

    const onSearch = (searchTermValue) => {
      // Debounce search
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTerm.value = searchTermValue;

      if (!searchTermValue || searchTermValue.trim() === '') {
        searchTerm.value = '';
        fetchSalesTypes();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        fetchSalesTypes();
      }, 300);
    };

    // Watchers
    watch(
      selectedSalesType,
      (newVal) => {
        emit('input', newVal?.id);
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      const salesTypeId = props.value;

      fetchSalesTypes();

      if (salesTypeId) {
        loadingSelectedSalesType.value = true;
        store
          .dispatch('sales-type-filter/fetchSalesType', { id: salesTypeId })
          .then((salesType) => {
            selectedSalesType.value = salesType;
          })
          .catch((error) => {
            console.log('error:', error);
          })
          .finally(() => {
            loadingSelectedSalesType.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      searchTerm,
      pageNumber,
      loadingSalesTypes,
      loadingSelectedSalesType,
      salesTypeOptions,
      selectedSalesType,
      searchTimeout,

      // Computed properties
      businessID,

      // Methods
      fetchSalesTypes,
      onSearch,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
